import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, UserCheck, Wand2, RefreshCw, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const calculateAge = (birthdate) => {
  if (!birthdate) return "N/A";
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export default function TeamAssignmentPanel() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [lastAutoAssignResult, setLastAutoAssignResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/academy/admin/unassigned-players`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true }),
      ]);
      setPlayers(playersRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load team assignment data");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const response = await axios.post(
        `${API}/academy/admin/auto-assign-teams`,
        {},
        { withCredentials: true }
      );
      setLastAutoAssignResult(response.data);
      toast.success(`Auto-assigned ${response.data.assigned_count} players!`);
      fetchData();
    } catch (error) {
      console.error("Error auto-assigning:", error);
      toast.error("Failed to auto-assign players");
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleAssignToTeam = async (playerId, teamId) => {
    if (!teamId) return;
    setAssigning(playerId);
    try {
      await axios.post(
        `${API}/academy/admin/assign-player-to-team?player_id=${playerId}&team_id=${teamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Player assigned to team");
      fetchData();
    } catch (error) {
      console.error("Error assigning player:", error);
      toast.error("Failed to assign player to team");
    } finally {
      setAssigning(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-12 bg-slate-900/50 flex justify-center" data-testid="team-assignment-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="team-assignment-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-bold text-white">Players Awaiting Team Assignment</h2>
          <Badge className="bg-orange-500/80 text-white">
            {players.length} pending
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-slate-900/50"
            data-testid="team-assignment-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAutoAssign}
            disabled={autoAssigning || players.length === 0}
            size="sm"
            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            data-testid="team-auto-assign-btn"
          >
            {autoAssigning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Auto-Assigning...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Auto-Assign All
              </>
            )}
          </Button>
        </div>
      </div>

      {lastAutoAssignResult && (
        <Card className="p-4 bg-slate-800/50 border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-white flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-purple-400" />
              Auto-Assignment Results
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setLastAutoAssignResult(null)}>✕</Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-emerald-500/20 rounded">
              <p className="text-xs text-slate-400">Assigned</p>
              <p className="text-2xl font-bold text-emerald-400">{lastAutoAssignResult.assigned_count}</p>
            </div>
            <div className="p-3 bg-amber-500/20 rounded">
              <p className="text-xs text-slate-400">Skipped</p>
              <p className="text-2xl font-bold text-amber-400">{lastAutoAssignResult.skipped_count}</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 bg-slate-900/50">
        {players.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white">All Players Assigned!</h3>
            <p className="text-slate-400 mt-2">Every registered player has been placed in a team.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Player</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Enrolled Sessions</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Assign Team</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {players.map((player) => (
                  <TableRow key={player.id} className="hover:bg-slate-800/30">
                    <TableCell>
                      <p className="font-medium text-white">
                        {player.child_name || player.name || `${player.given_name || ""} ${player.last_name || ""}`}
                      </p>
                      <p className="text-xs text-slate-400">{player.membership_number}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-bold">{calculateAge(player.birthdate)}</Badge>
                    </TableCell>
                    <TableCell>
                      {player.enrolled_sessions?.length > 0 ? (
                        <div className="space-y-1">
                          {player.enrolled_sessions.map((s, i) => (
                            <div key={i} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                              {s.day_of_week} · {s.age_group} <span className="text-slate-400">{s.time}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">No sessions</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-white">{player.parent_name || "N/A"}</p>
                      <p className="text-xs text-slate-400">{player.parent_email}</p>
                      <p className="text-xs text-slate-400">{player.parent_phone}</p>
                    </TableCell>
                    <TableCell>
                      {player.suggested_teams?.length > 0 && (
                        <p className="text-xs text-emerald-400 mb-1">
                          Suggested: {player.suggested_teams.map((t) => t.name).join(", ")}
                        </p>
                      )}
                      <select
                        className="text-sm border rounded px-3 py-2 min-w-[150px] bg-slate-900/50 border-slate-700 text-white"
                        defaultValue=""
                        disabled={assigning === player.id}
                        onChange={(e) => handleAssignToTeam(player.id, e.target.value)}
                        data-testid={`assign-team-select-${player.id}`}
                      >
                        <option value="">Select team...</option>
                        {player.suggested_teams?.length > 0 && (
                          <optgroup label="Suggested">
                            {player.suggested_teams.map((team) => (
                              <option key={`sug-${team.id}`} value={team.id}>⭐ {team.name}</option>
                            ))}
                          </optgroup>
                        )}
                        <optgroup label="All Teams">
                          {teams.map((team) => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </optgroup>
                      </select>
                      {assigning === player.id && (
                        <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
